import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2, MessageCircle, RotateCcw } from "lucide-react";
import { site } from "@/lib/site";
import { openPlanChat, type PlanKey } from "@/lib/plans";

export const Route = createFileRoute("/find-your-plan")({
  head: () => ({
    meta: [
      { title: "Find Your Plan - 60-Second Quiz | WeLivdIt" },
      { name: "description", content: "Answer 5 quick questions and get a personalized US visa coaching plan recommendation - Basic, Full, or Refusal Support." },
      { property: "og:title", content: "Find Your Plan - WeLivdIt" },
      { property: "og:description", content: "Take the 60-second plan finder and get a personalized recommendation for your US visa interview prep." },
      { property: "og:url", content: "https://welivdit.com/find-your-plan" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/find-your-plan" }],
  }),
  component: FindYourPlanPage,
});

type Lean = "basic" | "full" | "refusal";

type Option = {
  label: string;
  emoji: string;
  lean: Lean;
  reason: string; // shown on result screen if chosen
};

type Question = {
  id: string;
  title: string;
  subtitle?: string;
  options: Option[];
};

const questions: Question[] = [
  {
    id: "refused",
    title: "Have you been refused a US visa before?",
    subtitle: "Includes F-1, J-1, B1/B2, tourist, 214(b), or any other US visa category.",
    options: [
      { label: "Yes, I was refused", emoji: "❗", lean: "refusal", reason: "You've had a prior refusal - re-attempts need root-cause analysis and red-flag fixes." },
      { label: "No, this is my first time", emoji: "🆕", lean: "full", reason: "First-time applicant - end-to-end guidance gives you the strongest foundation." },
    ],
  },
  {
    id: "confidence",
    title: "How confident are you answering visa officer questions?",
    options: [
      { label: "Very confident - just need polish", emoji: "💪", lean: "basic", reason: "You're confident on content - focused mocks will sharpen delivery." },
      { label: "Somewhat - I freeze on tricky ones", emoji: "😬", lean: "full", reason: "Spontaneous question training will build composure under pressure." },
      { label: "Not confident at all", emoji: "😟", lean: "full", reason: "Officer-logic strategy + multiple mocks will get you interview-ready." },
    ],
  },
  {
    id: "ds160",
    title: "Have you completed your DS-160 form?",
    options: [
      { label: "Yes, fully done & reviewed", emoji: "✅", lean: "basic", reason: "DS-160 is sorted - no need to pay for guidance you don't need." },
      { label: "Started but unsure if it's correct", emoji: "📝", lean: "full", reason: "DS-160 review is included - small errors there cost approvals." },
      { label: "Not yet / need help filling it", emoji: "🆘", lean: "full", reason: "You'll get hands-on DS-160 guidance from start to submit." },
    ],
  },
  {
    id: "story",
    title: "How clear is your 'story'? (purpose, ties to home, finances)",
    options: [
      { label: "Crystal clear - 30-sec pitch ready", emoji: "🎯", lean: "basic", reason: "Your narrative is solid - mocks will keep it sharp." },
      { label: "I know it but struggle under pressure", emoji: "🤷", lean: "full", reason: "Profile analysis + officer-logic will tighten your story." },
      { label: "Haven't structured it yet", emoji: "❓", lean: "full", reason: "Profile analysis builds your narrative from the ground up." },
    ],
  },
  {
    id: "timeline",
    title: "When is your visa interview?",
    options: [
      { label: "Within 1–2 weeks", emoji: "📅", lean: "basic", reason: "Tight timeline - focused mocks deliver fast results." },
      { label: "3–6 weeks away", emoji: "🗓️", lean: "full", reason: "Ideal prep window for full end-to-end coaching." },
      { label: "6+ weeks / not booked yet", emoji: "⏳", lean: "full", reason: "Time on your side - use it for thorough prep." },
    ],
  },
];

type CorePlanKey = Extract<PlanKey, "basic" | "full" | "refusal">;
const planMeta: Record<CorePlanKey, { name: string; price: string; tag: string; href: "/basic-support" | "/full-support" | "/refusal-support" }> = {
  basic: { name: "Basic Support", price: "₹3,000", tag: "Focused mocks for confident applicants", href: "/basic-support" },
  full: { name: "Full Support", price: "₹4,500", tag: "End-to-end guidance - our most popular plan", href: "/full-support" },
  refusal: { name: "Refusal Support", price: "₹7,000", tag: "Re-attempt prep after a 214(b) refusal", href: "/refusal-support" },
};

function recommend(answers: (Option | null)[]): { plan: PlanKey; reasons: string[] } {
  // Q1 refusal is a hard override → Refusal Support (₹7,000), no matter any later answers.
  if (answers[0]?.lean === "refusal") {
    return {
      plan: "refusal",
      reasons: [answers[0].reason],
    };
  }

  // Keep a safety net: any refusal-leaning answer still forces Refusal Support.
  const refusalAnswers = answers.filter((a): a is Option => !!a && a.lean === "refusal");
  if (refusalAnswers.length > 0) {
    return {
      plan: "refusal",
      reasons: refusalAnswers.map((a) => a.reason),
    };
  }
  let basic = 0;
  let full = 0;
  for (const a of answers) {
    if (!a) continue;
    if (a.lean === "basic") basic++;
    else if (a.lean === "full") full++;
  }
  const plan: PlanKey = basic >= 3 ? "basic" : "full";
  const reasons = answers
    .filter((a): a is Option => !!a && a.lean === plan)
    .map((a) => a.reason)
    .slice(0, 3);
  return { plan, reasons };
}

function FindYourPlanPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(Option | null)[]>(() => questions.map(() => null));
  const [done, setDone] = useState(false);

  const total = questions.length;
  const progress = done ? 100 : Math.round((step / total) * 100);

  const handleSelect = (opt: Option) => {
    const next = [...answers];
    next[step] = opt;
    setAnswers(next);
    setTimeout(() => {
      // Refusal short-circuit: skip straight to result, no further questions needed.
      if (opt.lean === "refusal") {
        setDone(true);
        return;
      }
      if (step + 1 < total) setStep(step + 1);
      else setDone(true);
    }, 180);
  };

  const handleBack = () => {
    if (done) {
      setDone(false);
      return;
    }
    if (step > 0) setStep(step - 1);
  };

  const handleRestart = () => {
    setAnswers(questions.map(() => null));
    setStep(0);
    setDone(false);
  };

  const result = done ? recommend(answers) : null;
  const rec = result ? planMeta[result.plan as CorePlanKey] ?? null : null;

  return (
    <section className="bg-gradient-hero min-h-[calc(100vh-4rem)]">
      <div className="mx-auto max-w-2xl px-5 pt-12 pb-20 md:pt-20">
        <div className="text-center">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Plan Finder</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            {done ? "Your recommended plan" : "Find your perfect plan in 60 seconds"}
          </h1>
          {!done && (
            <p className="mt-4 text-foreground/70">5 quick questions - no email, no signup. Just an honest recommendation.</p>
          )}
        </div>

        {!done && (
          <div className="mt-8 space-y-4 text-left text-sm leading-relaxed text-foreground/75">
            <p>
              Most applicants overpay for coaching they do not need, or underprepare for the one thing that actually
              gets them refused. This 60-second plan finder exists to stop both. It reads your situation the same way a
              consular officer reads your case file - by looking for gaps between what you claim and what you can prove
              - and then matches you to the lightest plan that still closes those gaps.
            </p>
            <p>
              The quiz evaluates four things. First, <strong>DS-160 profile flags</strong>: mismatched addresses,
              unexplained travel history, previous refusals, an unrelated field of study, or university lists that do
              not line up with your verbal story. These are the errors officers spot before you finish your first
              answer. Second, <strong>study gaps and academic consistency</strong>: year drops, backlogs, career
              switches, or a master's that seems disconnected from your degree - each one raises the bar on everything
              else you say. Third, <strong>funding readiness</strong>: whether your sponsor, bank statements, ITR, and
              loan sanction letter tell one clean, memorised story, or three competing ones. Fourth,{" "}
              <strong>interview composure</strong>: how you hold up when the officer interrupts, repeats a question, or
              asks something you never rehearsed.
            </p>
            <p>
              Answer honestly and you will land on Basic Support, Full Support, or Refusal Support - with the reasoning
              shown, not hidden. Nothing is emailed, nothing is stored, and you can{" "}
              <Link to="/pricing" className="text-primary underline underline-offset-2">
                compare all plans and pricing
              </Link>{" "}
              or browse our{" "}
              <Link to="/resources" className="text-primary underline underline-offset-2">
                free F1 visa guides
              </Link>{" "}
              instead.
            </p>
          </div>
        )}


        {/* Progress bar */}
        <div className="mt-10">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{done ? "Complete" : `Question ${step + 1} of ${total}`}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-cta transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Quiz card */}
        {!done && (
          <div className="mt-8 rounded-3xl border border-border bg-card p-7 md:p-9 shadow-soft">
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-balance">
              {questions[step].title}
            </h2>
            {questions[step].subtitle && (
              <p className="mt-2 text-sm text-foreground/80">{questions[step].subtitle}</p>
            )}

            <div className="mt-6 grid gap-3">
              {questions[step].options.map((opt) => {
                const selected = answers[step]?.label === opt.label;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={
                      "group flex items-center gap-4 w-full text-left p-4 rounded-2xl border transition " +
                      (selected
                        ? "border-primary bg-primary/5 shadow-soft"
                        : "border-border bg-background hover:border-primary/40 hover:bg-secondary/40")
                    }
                  >
                    <span className="text-2xl shrink-0" aria-hidden>{opt.emoji}</span>
                    <span className="flex-1 text-sm md:text-base font-medium text-foreground/90">
                      {opt.label}
                    </span>
                    <ArrowRight className={"h-4 w-4 shrink-0 transition " + (selected ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                  </button>
                );
              })}
            </div>

            <div className="mt-7 flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={step === 0}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
                Skip - see all plans
              </Link>
            </div>
          </div>
        )}

        {/* Result card */}
        {done && rec && result && (
          <div className="mt-8 space-y-5">
            <div className="rounded-3xl border border-border bg-card p-7 md:p-9 shadow-soft">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5" /> Recommended for you
              </div>
              <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{rec.name}</h2>
                <span className="font-display text-2xl text-primary">{rec.price}</span>
              </div>
              <p className="mt-2 text-foreground/70">{rec.tag}</p>

              {result.reasons.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Why this plan fits you</p>
                  <ul className="mt-3 space-y-3">
                    {result.reasons.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground/85">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => openPlanChat(result.plan)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
                >
                  Enroll in {rec.name}
                </button>
                <Link
                  to={rec.href}
                  className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full border border-border bg-background text-foreground font-medium hover:bg-secondary transition"
                >
                  Learn what's included <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-muted/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Not sure? Talk to a human.</p>
                  <p className="text-sm text-muted-foreground">Quick WhatsApp chat - no obligation.</p>
                </div>
              </div>
              <a
                href={site.whatsapp}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition whitespace-nowrap"
              >
                Chat on WhatsApp
              </a>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={handleRestart}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4" /> Retake quiz
              </button>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
                Compare all plans →
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
